//ICS-203
import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { useSelector } from 'react-redux';
import { useGetRosterForIncident } from 'hooks/incident/useRoster';
import {
  useCurrentPeriod,
  useIncidentPermissions,
} from 'components/IAP/hooks/incidentHooks';
import LegacyPratusTable from 'components/DesignSystems/PratusTable/LegacyPratusTable';
import {
  useCreateIncidentAgency,
  useDeleteIncidentAgency,
  useIncidentAgencies,
  useUpdateIncidentAgency,
} from '../../hooks/incidentAgenciesHooks';
import {
  useCreateIncidentDivision,
  useDeleteIncidentDivision,
  useIncidentDivisions,
  useUpdateIncidentDivision,
} from '../../hooks/incidentDivisionHooks';
import {
  useCreateIncidentBranch,
  useDeleteIncidentBranch,
  useIncidentBranches,
  useUpdateIncidentBranch,
} from '../../hooks/incidentBranchHooks';
import {
  useIcsIncidentOrganizationAssignments,
  useUpsertIcsIncidentOrganizationAssignment,
} from '../../hooks/icsIncidentOrganizationAssignmentsHooks';
import { DebounceInput } from 'react-debounce-input';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { useSearchParams } from 'react-router';
import ICSWidget from './ICSWidget';
import { ConditionalCollapsibleCard } from 'components/IAP/ConditionalCollapsibleCard';
import { getIncidentCardTitle } from '../USCGPage';
import { ICS203_POSITION_KEYS } from 'constants/ics203PositionKeys';
import { RosterEntry } from '@/components/IAP/hooks/incidentRoster/incidentRoster.types';

const ROSTER_READONLY_SEATS = new Set<string>([
  'Incident Commander',
  'Incident Commander 2',
  'Incident Commander 3',
  'Incident Commander 4',
  'Incident Commander 5',
  'Safety Officer',
  'Public Information Officer',
  'Liaison Officer',
  'Planning Section Chief',
  'Resource Unit Leader',
  'Situation Unit Leader',
  'Environmental Unit Leader',
]);

const SEAT_MATCH_ALIASES: Record<string, string[]> = {
  'Public Information Officer': ['Information Officer'],
  'Resource Unit Leader': ['Resources Unit Leader'],
};

const ICS203_HEADER_DOC_TOOLTIP =
  'This form does not update the Incident Roster and is for documentation purposes only.';
const ICS203_ROSTER_FIELD_TOOLTIP =
  'This value is automatically populated with the user(s) in Incident Roster.';

function seatMatches(
  memberSeat: string[] | null | undefined,
  canonicalSeat: string
): boolean {
  if (!memberSeat) return false;
  const m = memberSeat.map((s) => s.toLowerCase());
  if (m.includes(canonicalSeat.toLowerCase())) return true;
  const aliases = SEAT_MATCH_ALIASES[canonicalSeat];
  return aliases?.some((a) => m.includes(a.toLowerCase())) ?? false;
}

function getRosterAssigneesDisplay(
  rosterData: RosterEntry[] | undefined,
  canonicalSeat: string
): { display: string; isAssigned: boolean } {
  if (!rosterData?.length) {
    return { display: 'Not Assigned', isAssigned: false };
  }
  const labels = rosterData
    .filter((member) =>
      seatMatches(
        member.incidentPositions.map((p) => p.positionName),
        canonicalSeat
      )
    )
    .map((m) => m.userName || m.emailAddress || 'Unknown')
    .filter(Boolean);
  const unique = [...new Set(labels)];
  if (unique.length === 0) {
    return { display: 'Not Assigned', isAssigned: false };
  }
  return { display: unique.join(', '), isAssigned: true };
}

type RosterReadonlyDisplayProps = {
  rosterData: RosterEntry[] | undefined;
  positionKey: string;
};

function RosterReadonlyDisplay({
  rosterData,
  positionKey,
}: RosterReadonlyDisplayProps) {
  const { display, isAssigned } = getRosterAssigneesDisplay(
    rosterData,
    positionKey
  );
  const tooltipId = `ics203-assignee-${positionKey.replace(/[^a-zA-Z0-9]+/g, '-')}`;
  return (
    <div className="d-flex align-items-center flex-wrap">
      <span className={isAssigned ? '' : 'text-weak'}>{display}</span>
      <OverlayTrigger
        placement="top"
        overlay={(props) => (
          <Tooltip id={tooltipId} {...props}>
            {ICS203_ROSTER_FIELD_TOOLTIP}
          </Tooltip>
        )}
      >
        <span
          className="d-inline-flex align-items-center text-weak ms-1"
          role="button"
          tabIndex={0}
          aria-label={ICS203_ROSTER_FIELD_TOOLTIP}
        >
          <SharedIcon iconName="info" size="sm" color="primary" />
        </span>
      </OverlayTrigger>
    </div>
  );
}

type FieldCellProps = {
  label: string;
  positionKey: string;
  rosterData: RosterEntry[] | undefined;
  positionAssignmentsByKey: Map<string, string>;
  onChange: (positionKey: string, value: string) => void;
  readOnly?: boolean;
};

function FieldCell({
  label,
  positionKey,
  rosterData,
  positionAssignmentsByKey,
  onChange,
  readOnly = false,
}: FieldCellProps) {
  const isRosterReadonly = ROSTER_READONLY_SEATS.has(positionKey);
  return (
    <div
      className="d-flex align-items-center gap-2"
      style={{ flex: '1 1 240px', minWidth: 0 }}
    >
      <span className="text-nowrap">{label}:</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {isRosterReadonly ? (
          <RosterReadonlyDisplay
            rosterData={rosterData}
            positionKey={positionKey}
          />
        ) : readOnly ? (
          <span className="text-weak">
            {positionAssignmentsByKey.get(positionKey) || '—'}
          </span>
        ) : (
          <DebounceInput
            debounceTimeout={2000}
            className="form-control"
            placeholder="Enter name..."
            value={positionAssignmentsByKey.get(positionKey) ?? ''}
            onChange={(e) => onChange(positionKey, e.target.value)}
            rows={1}
          />
        )}
      </div>
    </div>
  );
}

const OrganizationAssignmentList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const phase = searchParams.get('phase');
  const mode = searchParams.get('mode');
  const sectionKey = `${phase}-${mode}-organization-assignment-list`;

  const { periodNumber: currentPeriodNumber } = useCurrentPeriod();
  // Future tab is a planning period — no operational_period row exists yet,
  // so omit periodNumber and let the backend fall back to
  // ics_incident_info.next_operational_period_{start,anticipated_end}_date_time.
  const exportPeriodNumber =
    mode === 'future' ? undefined : currentPeriodNumber;

  const reduxCurrentIncident = useSelector(
    (state: any) => state.app.currentIncident
  );
  const incidentId =
    (reduxCurrentIncident && reduxCurrentIncident.id) || undefined;

  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;

  const { data: rosterData } = useGetRosterForIncident(incidentId);
  const { data: agenciesData } = useIncidentAgencies(incidentId);
  const mutation = useCreateIncidentAgency();
  const updateMutation = useUpdateIncidentAgency();
  const deleteMutation = useDeleteIncidentAgency();

  const { data: divisions } = useIncidentDivisions(incidentId);
  const { mutate: createIncidentDivision } = useCreateIncidentDivision();
  const { mutate: updateIncidentDivision } = useUpdateIncidentDivision();
  const { mutate: deleteIncidentDivision } = useDeleteIncidentDivision();

  const { data: branches } = useIncidentBranches(incidentId);
  const { mutate: createIncidentBranch } = useCreateIncidentBranch();
  const { mutate: updateIncidentBranch } = useUpdateIncidentBranch();
  const { mutate: deleteIncidentBranch } = useDeleteIncidentBranch();

  const { data: positionAssignments } =
    useIcsIncidentOrganizationAssignments(incidentId);
  const { mutate: upsertPositionAssignment } =
    useUpsertIcsIncidentOrganizationAssignment();

  const positionAssignmentsByKey = React.useMemo(() => {
    const map = new Map<string, string>();
    (positionAssignments ?? []).forEach((a) => {
      if (a.position_key) {
        map.set(a.position_key, a.name ?? '');
      }
    });
    return map;
  }, [positionAssignments]);

  const handlePositionChange = (positionKey: string, value: string) => {
    if (!incidentId) return;
    upsertPositionAssignment({
      incident_id: incidentId,
      position_key: positionKey,
      name: value,
    });
  };

  const fieldCellProps = {
    rosterData,
    positionAssignmentsByKey,
    onChange: handlePositionChange,
    readOnly: !canEditCards,
  };

  const incidentCommanderColumns: any[] = [
    {
      dataField: 'position',
      text: 'Incident Commander',
      formatter: (_position: string, row: any) => (
        <RosterReadonlyDisplay
          rosterData={rosterData}
          positionKey={row.position}
        />
      ),
    },
    {
      dataField: 'deputy',
      text: 'Deputy',
      formatter: (_value: string, row: any) => {
        const positionKey = `${row.position} Deputy`;
        if (!canEditCards) {
          return (
            <span className="text-weak">
              {positionAssignmentsByKey.get(positionKey) || '—'}
            </span>
          );
        }
        return (
          <DebounceInput
            debounceTimeout={2000}
            className="form-control"
            placeholder="Enter deputy.."
            value={positionAssignmentsByKey.get(positionKey) ?? ''}
            onChange={(e) => handlePositionChange(positionKey, e.target.value)}
            rows={1}
          />
        );
      },
    },
    {
      dataField: 'agency',
      text: 'Agency',
      formatter: (_value: string, row: any) => {
        const positionKey = `${row.position} Agency`;
        if (!canEditCards) {
          return (
            <span className="text-weak">
              {positionAssignmentsByKey.get(positionKey) || '—'}
            </span>
          );
        }
        return (
          <DebounceInput
            debounceTimeout={2000}
            className="form-control"
            placeholder="Enter agency..."
            value={positionAssignmentsByKey.get(positionKey) ?? ''}
            onChange={(e) => handlePositionChange(positionKey, e.target.value)}
            rows={1}
          />
        );
      },
    },
  ];
  const agencyColumns: any[] = [
    {
      dataField: 'agency_name',
      text: 'Agency / Organization',
      formatter: (_value: string, row: any) =>
        canEditCards ? (
          <DebounceInput
            debounceTimeout={2000}
            className="form-control"
            placeholder="Enter agency..."
            value={row.agency_name ?? ''}
            onChange={(e) =>
              updateMutation.mutate({
                ...row,
                agency_name: e.target.value,
              })
            }
            rows={1}
          />
        ) : (
          <span className="text-weak">{row.agency_name || '—'}</span>
        ),
    },
    {
      dataField: 'representative_name',
      text: 'Name',
      formatter: (_value: string, row: any) =>
        canEditCards ? (
          <DebounceInput
            debounceTimeout={2000}
            className="form-control"
            placeholder="Enter name.."
            value={row.representative_name ?? ''}
            onChange={(e) =>
              updateMutation.mutate({
                ...row,
                representative_name: e.target.value,
              })
            }
            rows={1}
          />
        ) : (
          <span className="text-weak">{row.representative_name || '—'}</span>
        ),
    },
    {
      dataField: 'id',
      text: '',
      formatter: (id: string) =>
        canEditCards ? (
          <StylishNewButton
            className="button button--link"
            onClick={() => {
              if (id) deleteMutation.mutate(id);
            }}
          >
            <SharedIcon iconName="delete" color="danger" />
          </StylishNewButton>
        ) : null,
    },
  ];
  const incidentCommanderSlots = [
    'Incident Commander',
    'Incident Commander 2',
    'Incident Commander 3',
    'Incident Commander 4',
    'Incident Commander 5',
  ];

  const incidentCommanderRow = incidentCommanderSlots
    .filter(
      (position) => getRosterAssigneesDisplay(rosterData, position).isAssigned
    )
    .map((position, idx) => ({
      id: idx + 1,
      position,
      symbol: 'star',
    }));

  const assignedIncidentCommanderCount = incidentCommanderRow.length;

  const intelligenceDivisions = (divisions || []).filter(
    (division) => division?.section === 'intelligence'
  );

  const { exportAsForm } = useIcsFormPdfPreview();
  const handleExport = (formName: string) => {
    void exportAsForm({
      formname: formName,
      data: {},
      mapSketch: null,
      periodNumber: exportPeriodNumber,
    });
  };
  const handleBranchDelete = (branch: any) => {
    if (branch.id) {
      deleteIncidentBranch(branch.id);
    }
    const divisionsToDelete = divisions?.filter(
      (division) => division.section === branch.id
    );
    if (divisionsToDelete && divisionsToDelete.length > 0) {
      divisionsToDelete.forEach((division) => {
        if (division.id) {
          deleteIncidentDivision(division.id);
        }
      });
    }
  };

  return (
    <ConditionalCollapsibleCard
      sectionKey={sectionKey}
      className="planning-widget"
      contentClassName="content mt-2"
      headerLeft={
        <ICSWidget.HeaderLeft
          iconName="people_outline"
          iconColor="primary"
          title={getIncidentCardTitle('organizationAssignmentList')}
          subtitle=""
          titleInfoTooltip={ICS203_HEADER_DOC_TOOLTIP}
        />
      }
      headerRight={
        <ICSWidget.HeaderRight
          icsRoles={[
            'IC/UC',
            'PSC',
            'Situation Unit Leader',
            'OSC',
            'RESL',
            'SOFR',
          ]}
          handleExport={() => handleExport('ICS_203_CG')}
          exportText="Preview ICS-203"
        />
      }
    >
      <div className="mb-2">Incident Commanders and Command Staff:</div>
      {incidentCommanderRow.length > 0 ? (
        <>
          <div className="planning-org-table">
            <LegacyPratusTable
              keyField="id"
              columns={incidentCommanderColumns}
              rows={incidentCommanderRow}
            />
          </div>
          <div className="text-weak mt-1 mb-3">
            {assignedIncidentCommanderCount} Incident Commander
            {assignedIncidentCommanderCount === 1 ? '' : 's'}
          </div>
        </>
      ) : (
        <div className="text-weak mb-3">No incident commanders assigned</div>
      )}
      <div className="d-flex mb-3 flex-wrap gap-3">
        <FieldCell
          {...fieldCellProps}
          label="Safety Officer"
          positionKey="Safety Officer"
        />
        <FieldCell
          {...fieldCellProps}
          label="Information Officer"
          positionKey="Public Information Officer"
        />
        <FieldCell
          {...fieldCellProps}
          label="Liaison Officer"
          positionKey="Liaison Officer"
        />
      </div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="text-nowrap">
          Agency / Organization Representatives:
        </span>
        {canEditCards && (
          <StylishNewButton
            className="button button--primary gap-1"
            onClick={() =>
              mutation.mutate({
                incident_id: incidentId,
                agency_name: '',
                representative_name: '',
              })
            }
            disabled={agenciesData && agenciesData.length >= 4}
          >
            <SharedIcon iconName="add" size="sm" /> Add Agency
          </StylishNewButton>
        )}
      </div>
      {agenciesData && agenciesData.length > 0 ? (
        <>
          <div className="planning-org-table">
            <LegacyPratusTable
              keyField="id"
              columns={agencyColumns}
              rows={agenciesData}
            />
          </div>
          <div className="text-weak mt-1 mb-3">
            {agenciesData.length} Agenc
            {agenciesData.length === 1 ? 'y' : 'ies'}
          </div>
        </>
      ) : (
        <div className="text-weak mb-3">No agencies added</div>
      )}
      <div className="mt-3">
        <div className="mb-1">Planning Section:</div>
        <hr className="mt-0 mb-3" />
        <div className="d-flex mb-3 flex-wrap gap-3">
          <FieldCell
            {...fieldCellProps}
            label="Chief"
            positionKey="Planning Section Chief"
          />
          <FieldCell
            {...fieldCellProps}
            label="Deputy"
            positionKey={ICS203_POSITION_KEYS.planningSectionDeputy}
          />
          <FieldCell
            {...fieldCellProps}
            label="Resources Unit"
            positionKey="Resource Unit Leader"
          />
          <FieldCell
            {...fieldCellProps}
            label="Situation Unit"
            positionKey="Situation Unit Leader"
          />
          <FieldCell
            {...fieldCellProps}
            label="Environmental Unit"
            positionKey="Environmental Unit Leader"
          />
          <FieldCell
            {...fieldCellProps}
            label="Documentation Unit"
            positionKey={ICS203_POSITION_KEYS.documentationUnitLeader}
          />
          <FieldCell
            {...fieldCellProps}
            label="Demobilization Unit"
            positionKey={ICS203_POSITION_KEYS.demobilizationUnitLeader}
          />
          <FieldCell
            {...fieldCellProps}
            label="Technical Specialist"
            positionKey={ICS203_POSITION_KEYS.technicalSpecialists}
          />
        </div>
      </div>
      <div className="mt-3">
        <div className="mb-1">Logistics Section:</div>
        <hr className="mt-0 mb-3" />
        <div className="d-flex mb-3 flex-wrap gap-3">
          <FieldCell
            {...fieldCellProps}
            label="Chief"
            positionKey={ICS203_POSITION_KEYS.logisticSectionChief}
          />
          <FieldCell
            {...fieldCellProps}
            label="Deputy"
            positionKey={ICS203_POSITION_KEYS.logisticSectionDeputy}
          />
        </div>
        <div className="d-flex mb-2 flex-wrap gap-3">
          <FieldCell
            {...fieldCellProps}
            label="Support Branch"
            positionKey={ICS203_POSITION_KEYS.supportBranchName}
          />
        </div>
        <div className="border-start mb-3 ps-4">
          <div className="d-flex flex-wrap gap-3">
            <FieldCell
              {...fieldCellProps}
              label="Director"
              positionKey={ICS203_POSITION_KEYS.supportBranchDirector}
            />
            <FieldCell
              {...fieldCellProps}
              label="Supply Unit"
              positionKey={ICS203_POSITION_KEYS.supplyUnitLeader}
            />
            <FieldCell
              {...fieldCellProps}
              label="Facilities Unit"
              positionKey={ICS203_POSITION_KEYS.facilitiesUnitLeader}
            />
          </div>
        </div>
        <div className="d-flex mb-2 flex-wrap gap-3">
          <FieldCell
            {...fieldCellProps}
            label="Service Branch"
            positionKey={ICS203_POSITION_KEYS.serviceBranchName}
          />
        </div>
        <div className="border-start mb-3 ps-4">
          <div className="d-flex flex-wrap gap-3">
            <FieldCell
              {...fieldCellProps}
              label="Director"
              positionKey={ICS203_POSITION_KEYS.serviceBranchDirector}
            />
            <FieldCell
              {...fieldCellProps}
              label="Communications Unit"
              positionKey={ICS203_POSITION_KEYS.communicationUnitLeader}
            />
            <FieldCell
              {...fieldCellProps}
              label="Medical Unit"
              positionKey={ICS203_POSITION_KEYS.medicalUnitLeader}
            />
          </div>
        </div>
      </div>
      <div className="mt-3">
        <div className="mb-1">Operations Section:</div>
        <hr className="mt-0 mb-3" />
        <div className="d-flex mb-3 flex-wrap gap-3">
          <FieldCell
            {...fieldCellProps}
            label="Chief"
            positionKey={ICS203_POSITION_KEYS.operationSectionChief}
          />
          <FieldCell
            {...fieldCellProps}
            label="Deputy"
            positionKey={ICS203_POSITION_KEYS.operationSectionDeputy}
          />
          <FieldCell
            {...fieldCellProps}
            label="Staging Area Manager"
            positionKey={ICS203_POSITION_KEYS.stagingAreaManager1}
          />
          <FieldCell
            {...fieldCellProps}
            label="Staging Area Manager"
            positionKey={ICS203_POSITION_KEYS.stagingAreaManager2}
          />
          <FieldCell
            {...fieldCellProps}
            label="Staging Area Manager"
            positionKey={ICS203_POSITION_KEYS.stagingAreaManager3}
          />
        </div>
        {canEditCards && (
          <div className="d-flex justify-content-end align-items-center mb-2 gap-2">
            <StylishNewButton
              onClick={() =>
                createIncidentBranch({
                  incident_id: incidentId,
                  name: '',
                  branch_director_name: '',
                  branch_deputy: '',
                  division_list: [],
                })
              }
              disabled={branches && branches.length >= 2}
              className="button button--primary gap-1"
            >
              <SharedIcon iconName="add" size="sm" /> Add Branch
            </StylishNewButton>
          </div>
        )}
        {branches && branches.length > 0
          ? branches.map((branch) => {
              const branchDivisions = (divisions || [])
                .filter((d) => d?.section === branch?.id)
                .sort((a, b) => (a.id || '').localeCompare(b.id || ''));
              return (
                <div key={branch.id} className="mb-3">
                  <div className="d-flex align-items-center mb-2 flex-wrap gap-3">
                    <div
                      className="d-flex align-items-center gap-2"
                      style={{ flex: '1 1 240px', minWidth: 0 }}
                    >
                      <span className="text-nowrap">Branch:</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <DebounceInput
                          debounceTimeout={2000}
                          className="form-control"
                          placeholder="Enter name..."
                          value={branch.name || ''}
                          disabled={!canEditCards}
                          onChange={(e) =>
                            updateIncidentBranch({
                              ...branch,
                              name: e.target.value,
                            })
                          }
                          rows={1}
                        />
                      </div>
                    </div>
                    {canEditCards && (
                      <StylishNewButton
                        className="button button--link"
                        onClick={() => handleBranchDelete(branch)}
                      >
                        <SharedIcon iconName="delete" color="danger" />
                      </StylishNewButton>
                    )}
                  </div>
                  <div className="border-start ps-4">
                    <div className="d-flex mb-2 flex-wrap gap-3">
                      <div
                        className="d-flex align-items-center gap-2"
                        style={{ flex: '1 1 240px', minWidth: 0 }}
                      >
                        <span className="text-nowrap">Branch Director:</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <DebounceInput
                            debounceTimeout={2000}
                            className="form-control"
                            placeholder="Enter name..."
                            value={branch.branch_director_name || ''}
                            disabled={!canEditCards}
                            onChange={(e) =>
                              updateIncidentBranch({
                                ...branch,
                                branch_director_name: e.target.value,
                              })
                            }
                            rows={1}
                          />
                        </div>
                      </div>
                      <div
                        className="d-flex align-items-center gap-2"
                        style={{ flex: '1 1 240px', minWidth: 0 }}
                      >
                        <span className="text-nowrap">Deputy:</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <DebounceInput
                            debounceTimeout={2000}
                            className="form-control"
                            placeholder="Enter name..."
                            value={branch.branch_deputy || ''}
                            disabled={!canEditCards}
                            onChange={(e) =>
                              updateIncidentBranch({
                                ...branch,
                                branch_deputy: e.target.value,
                              })
                            }
                            rows={1}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="d-flex mb-2 flex-wrap gap-3">
                      {branchDivisions.map((division) => (
                        <div
                          key={division.id}
                          className="d-flex align-items-center gap-2"
                          style={{ flex: '1 1 240px', minWidth: 0 }}
                        >
                          <span className="text-nowrap">Division / Group:</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <DebounceInput
                              debounceTimeout={2000}
                              className="form-control"
                              placeholder="Enter name..."
                              value={division.name || ''}
                              disabled={!canEditCards}
                              onChange={(e) =>
                                updateIncidentDivision({
                                  ...division,
                                  name: e.target.value,
                                })
                              }
                              rows={1}
                            />
                          </div>
                          {canEditCards && (
                            <StylishNewButton
                              className="button button--link"
                              onClick={() =>
                                division.id &&
                                deleteIncidentDivision(division.id)
                              }
                            >
                              <SharedIcon iconName="delete" color="danger" />
                            </StylishNewButton>
                          )}
                        </div>
                      ))}
                    </div>
                    {canEditCards && (
                      <div className="d-flex justify-content-end mb-2">
                        <StylishNewButton
                          className="button button--primary gap-1"
                          onClick={() => {
                            if (branch.id) {
                              createIncidentDivision({
                                incident_id: incidentId,
                                name: '',
                                division_supervisor_name: '',
                                section: branch.id,
                              });
                            }
                          }}
                          disabled={branchDivisions.length >= 6}
                        >
                          <SharedIcon iconName="add" size="sm" /> Add Division /
                          Group
                        </StylishNewButton>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          : ''}
      </div>
      <div className="mt-3">
        <div className="mb-1">Intelligence / Investigations Section:</div>
        <hr className="mt-0 mb-3" />
        <div className="d-flex mb-3 flex-wrap gap-3">
          <FieldCell
            {...fieldCellProps}
            label="Chief"
            positionKey={ICS203_POSITION_KEYS.intelligenceUnitLeader}
          />
          <FieldCell
            {...fieldCellProps}
            label="Deputy"
            positionKey={ICS203_POSITION_KEYS.intelligenceDeputy}
          />
        </div>
        <div className="d-flex mb-2 flex-wrap gap-3">
          {intelligenceDivisions.map((division) => (
            <div
              key={division.id}
              className="d-flex align-items-center gap-2"
              style={{ flex: '1 1 240px', minWidth: 0 }}
            >
              <span className="text-nowrap">Division / Group:</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <DebounceInput
                  debounceTimeout={2000}
                  className="form-control"
                  placeholder="Enter name..."
                  value={division.name || ''}
                  disabled={!canEditCards}
                  onChange={(e) =>
                    updateIncidentDivision({
                      ...division,
                      name: e.target.value,
                    })
                  }
                  rows={1}
                />
              </div>
              {canEditCards && (
                <StylishNewButton
                  className="button button--link"
                  onClick={() =>
                    division.id && deleteIncidentDivision(division.id)
                  }
                >
                  <SharedIcon iconName="delete" color="danger" />
                </StylishNewButton>
              )}
            </div>
          ))}
        </div>
        {canEditCards && (
          <div className="d-flex justify-content-end mb-2">
            <StylishNewButton
              className="button button--primary gap-1"
              onClick={() =>
                createIncidentDivision({
                  incident_id: incidentId,
                  name: '',
                  division_supervisor_name: '',
                  section: 'intelligence',
                })
              }
              disabled={intelligenceDivisions.length >= 6}
            >
              <SharedIcon iconName="add" size="sm" /> Add Division / Group
            </StylishNewButton>
          </div>
        )}
      </div>
      <div className="mt-3">
        <div className="mb-1">Finance / Administration Section:</div>
        <hr className="mt-0 mb-3" />
        <div className="d-flex mb-3 flex-wrap gap-3">
          <FieldCell
            {...fieldCellProps}
            label="Chief"
            positionKey={ICS203_POSITION_KEYS.financeSectionChief}
          />
          <FieldCell
            {...fieldCellProps}
            label="Deputy"
            positionKey={ICS203_POSITION_KEYS.financeSectionDeputy}
          />
          <FieldCell
            {...fieldCellProps}
            label="Time Unit"
            positionKey={ICS203_POSITION_KEYS.timeUnitLeader}
          />
          <FieldCell
            {...fieldCellProps}
            label="Procurement Unit"
            positionKey={ICS203_POSITION_KEYS.procurementUnitLeader}
          />
          <FieldCell
            {...fieldCellProps}
            label="Comp / Claims Unit"
            positionKey={ICS203_POSITION_KEYS.compensationUnitLeader}
          />
          <FieldCell
            {...fieldCellProps}
            label="Cost Unit"
            positionKey={ICS203_POSITION_KEYS.costUnitLeader}
          />
        </div>
      </div>
    </ConditionalCollapsibleCard>
  );
};

export default OrganizationAssignmentList;
